// Order sheet column filters, as Excel's: each column can be narrowed to some of its values, "(Blanks)"
// and "+" among them, and a vessel shows when it passes every column's filter. A cell that's empty or
// only a separator ('.', '-', '_') counts as blank, as it does in the Procurement totals.

import { isBlankCell } from './procurement.js';

/** the "(Blanks)" value. */
export const BLANK = '';

/** column id → the values kept. A column with no entry isn't filtered. */
export type ColumnFilters = Readonly<Record<string, readonly string[]>>;

/** the value a cell is filtered by: '' for a blank one, else what's written (trimmed). */
export const filterValue = (raw: string | null | undefined): string => (isBlankCell(raw) ? BLANK : raw!.trim());

export interface ValueCount {
  value: string;
  count: number;
}

/** a column's values and how many rows have each: blanks first, then "+", then the most common. Values
 *  still ticked in `keep` stay listed (at 0) so they can be unticked once no row has them. */
export function columnValues<R>(rows: readonly R[], valueOf: (row: R) => string, keep: readonly string[] = []): ValueCount[] {
  const counts = new Map<string, number>();
  for (const r of rows) {
    const v = valueOf(r);
    counts.set(v, (counts.get(v) ?? 0) + 1);
  }
  for (const k of keep) if (!counts.has(k)) counts.set(k, 0);
  const rank = (v: string) => (v === BLANK ? 0 : v === '+' ? 1 : 2);
  return [...counts.entries()]
    .map(([value, count]) => ({ value, count }))
    .sort((a, b) => rank(a.value) - rank(b.value) || b.count - a.count || a.value.localeCompare(b.value, undefined, { numeric: true }));
}

/** whether a row passes every column filter (but `skip`'s, when listing that column's own values). */
export function passesFilters<R>(row: R, filters: ColumnFilters, valueOf: (row: R, col: string) => string, skip?: string): boolean {
  for (const [col, keep] of Object.entries(filters)) {
    if (col !== skip && !keep.includes(valueOf(row, col))) return false;
  }
  return true;
}

/** a column's filter after a change: dropped when every listed value is kept (nothing left to filter). */
export function setColumnFilter(filters: ColumnFilters, col: string, keep: readonly string[] | null, listed: readonly ValueCount[]): ColumnFilters {
  const { [col]: _old, ...rest } = filters;
  if (keep == null || listed.every((v) => keep.includes(v.value))) return rest;
  return { ...rest, [col]: [...keep] };
}
